import React from 'react';
import { REXY_FONT, REXY_CONSOLE } from '../../lib/rexyConsole';
import { useNotes, NOTE_KIND_LABEL as KIND_LABEL } from '../../data/dashboard';
import type { NoteHour } from '../../data/dashboard';
import { useCollapsed, PanelTitle } from './PanelCollapse';

const MONO = REXY_FONT;
const NOTE_OK = '#00FF9C';

// ─────────────────────────────────────────────────────────────────────────────
// NOTES — ONE 1-2 SENTENCE REFLECTION PER EVENT (§5C), grouped under the pulse hour.
// Each event she reads gets its OWN substantive note (the trading event's real
// trades/R/%, the security event's real rejections) — not one collapsed gloss per
// pulse. content is the verbatim Tier-2 artifact text; the UI mirrors it exactly.
//
// This file is the DESKTOP ARRANGEMENT ONLY. The wiring (endpoint, poll, clock,
// payload types) moved to data/dashboard/useNotes.ts under the 2026-07-31 "one
// core, every window" ruling — the portrait phone surface renders the SAME feed
// in a different arrangement. The render below is unchanged.
// ─────────────────────────────────────────────────────────────────────────────

// Types re-exported so existing importers of this module keep resolving.
export type {
  NoteEntry, NoteHour, NotesView, NotesResponse,
} from '../../data/dashboard';

const STATUS: Record<NoteHour['status'], { glyph: string; text: string; color: string }> = {
  written: { glyph: '✓', text: 'written', color: NOTE_OK },
  partial: { glyph: '±', text: 'partial', color: REXY_CONSOLE.warning },
  empty: { glyph: '○', text: 'honestly empty', color: REXY_CONSOLE.dimText },
  failed: { glyph: '✗', text: 'failed', color: REXY_CONSOLE.critical },
  unreadable: { glyph: '!', text: 'unreadable', color: REXY_CONSOLE.critical },
  missing: { glyph: '·', text: 'missing', color: REXY_CONSOLE.warning },
};

/** `collapseKey`: the homepage mount's fold control and its stored choice (PanelCollapse). */
export default function NotesPanel({ collapseKey }: { collapseKey?: string } = {}) {
  const { notes, err, updatedAt, hourLabel, stampLabel } = useNotes();
  const [collapsed, toggle] = useCollapsed(collapseKey);

  function renderHour(hour: NoteHour) {
    const state = STATUS[hour.status];
    const n = hour.notes.length;
    return (
      <div key={hour.slot_id} style={styles.hour} title={hour.slot_id}>
        <div style={styles.hourHeader}>
          <span style={styles.hourLabel}>{hourLabel(hour.scheduled_for)}</span>
          <span style={{ ...styles.glyph, color: state.color }}>{state.glyph}</span>
          <span style={{ ...styles.status, color: state.color }}>{state.text}</span>
          {n > 0 ? <span style={styles.count}>{n} note{n === 1 ? '' : 's'}</span> : null}
          {hour.failed ? <span style={styles.failCount}>{hour.failed} failed</span> : null}
        </div>
        {/* One note PER EVENT — each her own substantive 1-2 sentence reflection, verbatim. */}
        {hour.notes.length > 0 ? (
          <div style={styles.noteList}>
            {hour.notes.map((ev) => (
              <div key={ev.note_id} style={styles.noteRow}>
                <div style={styles.noteMeta}>
                  <span style={styles.noteWho}>{ev.who || 'the OS'}</span>
                  {ev.where ? (
                    <span style={styles.noteWhere}>
                      {ev.source_kind ? `${KIND_LABEL[ev.source_kind] || ev.source_kind} · ` : ''}
                      {ev.where}
                    </span>
                  ) : null}
                </div>
                <div style={styles.noteText}>{ev.content}</div>
              </div>
            ))}
          </div>
        ) : hour.status === 'empty' ? null : (
          <div style={styles.failure}>{hour.error || `note ${state.text}`}</div>
        )}
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={collapsed ? { ...styles.headerRow, marginBottom: 0 } : styles.headerRow}>
        <PanelTitle title="NOTES" style={styles.sectionHeader} collapseKey={collapseKey}
                    collapsed={collapsed} onToggle={toggle} />
        <div style={styles.subtitle}>
          per-event {'·'} {notes?.timezone || 'world clock'}
          {updatedAt ? <span> {'·'} updated {stampLabel(updatedAt)}</span> : null}
        </div>
      </div>
      {collapsed ? null : err ? (
        <div style={styles.degraded}>not_available {'—'} {err}</div>
      ) : !notes ? (
        <div style={styles.degraded}>loading{'…'}</div>
      ) : notes.ledger_status === 'unreadable' ? (
        <div style={styles.critical}>note ledger unreadable {'—'} visible failure</div>
      ) : notes.hours.length === 0 ? (
        <div style={styles.emptyHistory}>no note outcomes recorded yet</div>
      ) : (
        <div style={styles.hours}>{notes.hours.map(renderHour)}</div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  card: { border: `1px solid ${REXY_CONSOLE.cardBorder}`, background: REXY_CONSOLE.cardBg, borderRadius: 6, padding: 14, boxShadow: '0 4px 14px rgba(0,0,0,0.45), inset 0 1px 0 rgba(0,191,255,0.06)', minWidth: 0, maxWidth: '100%', overflowWrap: 'anywhere' },
  // Title sits at the TOP of the panel; the meta strap sits UNDER it and wraps, so it
  // can never displace the title or overflow the card edge (owner ruling 2026-07-24).
  headerRow: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 3, marginBottom: 10, minWidth: 0 },
  sectionHeader: { fontWeight: 700, fontFamily: MONO, fontSize: 12, letterSpacing: 2, color: REXY_CONSOLE.primary },
  subtitle: { fontFamily: MONO, fontSize: 9, letterSpacing: 1, color: REXY_CONSOLE.dimText, whiteSpace: 'normal', overflowWrap: 'anywhere', maxWidth: '100%' },
  hours: { display: 'flex', flexDirection: 'column', gap: 8 },
  hour: { borderTop: `1px solid ${REXY_CONSOLE.rowDivider}`, padding: '7px 4px 2px 4px' },
  hourHeader: { display: 'flex', alignItems: 'baseline', gap: 8 },
  hourLabel: { fontFamily: MONO, fontSize: 10, color: REXY_CONSOLE.mutedText, minWidth: 36 },
  glyph: { fontFamily: MONO, fontSize: 11 },
  status: { fontFamily: MONO, fontSize: 9, letterSpacing: 1 },
  count: { fontFamily: MONO, fontSize: 9, color: REXY_CONSOLE.dimText, marginLeft: 'auto' },
  failCount: { fontFamily: MONO, fontSize: 9, color: REXY_CONSOLE.critical },
  noteList: { display: 'flex', flexDirection: 'column', gap: 6, marginTop: 5 },
  noteRow: { borderLeft: `2px solid ${REXY_CONSOLE.divider}`, paddingLeft: 7, paddingBottom: 2 },
  noteMeta: { display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 2 },
  noteWho: { fontFamily: MONO, fontSize: 9, color: REXY_CONSOLE.primary, whiteSpace: 'nowrap' },
  noteWhere: { fontFamily: MONO, fontSize: 8, color: REXY_CONSOLE.dimText, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 },
  noteText: { fontFamily: MONO, fontSize: 10, lineHeight: 1.5, color: REXY_CONSOLE.infText, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' },
  failure: { fontFamily: MONO, fontSize: 9, color: REXY_CONSOLE.critical, marginTop: 4 },
  critical: { fontFamily: MONO, fontSize: 10, color: REXY_CONSOLE.critical, padding: '4px 0' },
  degraded: { fontFamily: MONO, fontSize: 10, color: REXY_CONSOLE.dimText, padding: '4px 0' },
  emptyHistory: { fontFamily: MONO, fontSize: 9, color: REXY_CONSOLE.dimText, padding: '4px 0' },
};
